import * as React from 'react';

// Constants & Enums
import { ErrorCode } from '@shopee-user/api-constants';
import { ShopeeAuthProvider } from './constants';
import { AuthenticateChannel } from '../../core/types';

// Utils
import { isWebBridgeSupported, useTranslation } from '@shopee-user/platform';
import { AuthMethod, openExternalLink } from '@shopee/web-bridge-sdk';
import { createTobSdkEventCallbackListener } from '../../../web-bridge-listener';
import {
  getShopeeOAuthConfig,
  getWebThirdPartyClientId,
} from '@shopee-user/business-customization-logic';
import { getShopeeAppOAuthUrl, getShopeeWebOAuthUrl } from './utils';
import logInNonLive, {
  logErrorInNonLive,
} from '@tob/shared/utils/log-in-nonlive';
import {
  setCurrentUrl,
  replaceCurrentUrl,
} from '@tob/shared/utils/redirection';
import { createEncryptedState } from '@tob/shared/security/oauth-state';
import { setThirdPartyOAuthContextToStorage } from '../../../../pages/third-party-service/sessionStorage';
import { ThirdpartyBindingEntryPoint } from '../../service/constants';

// Types
import type { WithUserWebViewProps } from '@shopee-user/with-web-view';

export const useConnectWithShopee = ({
  webViewGetAuthConfig,
  entryPoint,
  shouldReplaceCurrentUrl = false,
}: {
  webViewGetAuthConfig: WithUserWebViewProps['webViewGetAuthConfig'];
  entryPoint: ThirdpartyBindingEntryPoint;
  shouldReplaceCurrentUrl?: boolean;
}) => {
  const tobSdkEventListener = React.useRef<ReturnType<
    typeof createTobSdkEventCallbackListener
  > | null>(null);
  const { currentLanguage, t } = useTranslation();

  React.useEffect(() => {
    return () => {
      if (tobSdkEventListener.current) {
        tobSdkEventListener.current.clear();
      }
    };
  }, []);

  const authenticate = async ({
    webAuthCodeConsumePageUrl,
  }: {
    webAuthCodeConsumePageUrl: string;
  }): Promise<{
    authProvider: ShopeeAuthProvider;
    authCode?: string;
    appClientId?: string;
    isCancel?: boolean;
    error?: number; // TODO: we might need to differentiate the error for different failure cases
    errorMsg?: string;
  }> => {
    if (isWebBridgeSupported()) {
      const result = await authenticateByShopeeApp();
      return {
        ...result,
        authProvider: ShopeeAuthProvider.ShopeeApp,
      };
    } else {
      await authenticateByShopeeWeb({
        authCodeConsumePageUrl: webAuthCodeConsumePageUrl,
      });
      return {
        authProvider: ShopeeAuthProvider.ShopeeWeb,
      };
    }
  };

  const authenticateByShopeeApp = async () => {
    const { config, errorCode, errorMessage } = await webViewGetAuthConfig({
      method: AuthMethod.ShopeeOAuth,
    });

    const {
      enabled: supported,
      client_id: clientId,
      redirect_url: hostAppCallbackUrl,
    } = config || {};

    const { scopes, displayNameTransifyKey } = getShopeeOAuthConfig();

    if (!clientId || errorCode || errorMessage) {
      return {
        error: errorCode || 1,
        errorMsg: errorMessage,
      };
    }

    if (!hostAppCallbackUrl) {
      return {
        error: 1,
        errorMsg: '[Shopee] Callback URL is undefined',
      };
    }

    return new Promise<{
      authCode?: string;
      appClientId?: string;
      isCancel?: boolean;
      error?: number;
      errorMsg?: string;
    }>(resolve => {
      const url = supported
        ? getShopeeAppOAuthUrl({
            clientId,
            scopes,
            displayName: t(displayNameTransifyKey),
            appCallbackUrl: hostAppCallbackUrl,
          })
        : null;
      if (url) {
        try {
          // https://confluence.shopee.io/x/G0L_q#heading-221bToBSDKxTobWeb
          tobSdkEventListener.current = createTobSdkEventCallbackListener({
            scene: 'shopee_oauth',
            onCallback: ({ data }: { data: Record<string, any> }) => {
              logInNonLive(
                'clear tob event listener because of success -> shopee_oauth'
              );
              tobSdkEventListener.current?.clear();
              const authCode = data?.authCode;
              if (authCode) {
                resolve({ authCode, appClientId: clientId });
              } else {
                resolve({
                  appClientId: clientId,
                  isCancel: true,
                });
              }
            },
            onError: () => {
              logErrorInNonLive(
                'clear tob event listener because of error -> shopee_oauth'
              );
              tobSdkEventListener.current?.clear();
              resolve({
                appClientId: clientId,
                error: ErrorCode.FeErrorCode,
                errorMsg:
                  'Failed to create Shopee app oauth callback event listener',
              });
            },
          });
          logInNonLive('register tob event listener -> shopee_oauth');
          tobSdkEventListener.current?.register();
          logInNonLive('openExternalLink', url);
          openExternalLink({
            url,
            popSelf: false,
            useInAppBrowser: false,
          });
        } catch (err) {
          resolve({
            appClientId: clientId,
            error: ErrorCode.FeErrorCode,
            errorMsg: `unexpected exception: ${err.message || err}`,
          });
        }
      } else {
        resolve({
          appClientId: clientId,
          error: ErrorCode.FeErrorCode,
          errorMsg: 'Failed to generate Shopee app oauth url',
        });
      }
    });
  };

  const authenticateByShopeeWeb = async ({
    authCodeConsumePageUrl,
  }: {
    authCodeConsumePageUrl: string;
  }) => {
    // without passing webbridge, getShopeeOAuthConfig will return web config
    const { scopes } = getShopeeOAuthConfig();
    const clientId = getWebThirdPartyClientId({
      channel: AuthenticateChannel.Shopee,
    });

    const url = getShopeeWebOAuthUrl({
      clientId,
      scopes,
      authCodeConsumePageUrl,
      language: currentLanguage,
      entryPoint,
    });

    if (url) {
      // Create encrypted state for Shopee OAuth (unified with Google/Facebook)
      const { state, keyJwk } = await createEncryptedState({
        type: AuthenticateChannel.Shopee,
      });

      // Store OAuth context using unified storage
      setThirdPartyOAuthContextToStorage({
        oauthState: state,
        oauthStateKey: keyJwk,
        clientId,
        triggerUrl: window.location.href,
        entryPoint,
      });

      if (shouldReplaceCurrentUrl) {
        replaceCurrentUrl({ url, isHardcodedUrl: true });
      } else {
        setCurrentUrl({ url, isHardcodedUrl: true });
      }
      return { clientId };
    } else {
      return {
        error: ErrorCode.FeErrorCode,
        errorMsg: 'Failed to generate Shopee web oauth url',
      };
    }
  };

  return {
    authenticate,
  };
};
